import { HttpContextToken, HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { RuntimeConfigService } from '../config/runtime-config';
import { ApiError, toApiError } from '../http/api-error';
import { isApiRequest, isAuthEndpoint } from '../http/api-url';
import { t } from '../i18n/t';
import { ToastService } from '../services/toast.service';

/** Set on a request whose caller shows its own error, so no toast is raised for it. */
export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

function problemCode(error: HttpErrorResponse): string {
  const body: unknown = error.error;
  return typeof body === 'object' && body !== null && 'code' in body
    ? String((body as { code: unknown }).code)
    : '';
}

/**
 * On a 401, asks the server to refresh the session and replays the call once. If the refresh
 * is refused, the live session is ended and the user goes to sign in. A wrong password is
 * also a 401 and must not trigger any of this, and neither may the auth calls themselves.
 *
 * Registered OUTSIDE the CSRF interceptor so the replay re-reads the CSRF cookie, which the
 * refresh has just rotated.
 */
export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const { apiBaseUrl } = inject(RuntimeConfigService).config();
  if (!isApiRequest(req.url, apiBaseUrl) || isAuthEndpoint(req.url)) return next(req);
  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (
        !(error instanceof HttpErrorResponse) ||
        error.status !== 401 ||
        problemCode(error) === 'auth.invalid_credentials'
      ) {
        return throwError(() => error);
      }
      return from(auth.refresh()).pipe(
        switchMap((refreshed) => {
          if (refreshed) return next(req);
          auth.sessionEnded();
          return throwError(() => error);
        }),
      );
    }),
  );
};

/**
 * Turns every failed API call into an `ApiError` and, for failures a person cannot fix on
 * the form, shows one plain-English toast: no network, forbidden, rate limited, server
 * trouble. Validation, not-found and conflict errors are left to the screen that asked.
 * Session expiry is handled by `refreshInterceptor` and gets no second toast here.
 *
 * Registered OUTERMOST so it sees the final outcome after any refresh and replay.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const { apiBaseUrl } = inject(RuntimeConfigService).config();
  if (!isApiRequest(req.url, apiBaseUrl)) return next(req);
  const toast = inject(ToastService);

  return next(req).pipe(
    catchError((error: unknown) => {
      const apiError = toApiError(error);
      if (!req.context.get(SUPPRESS_ERROR_TOAST) && needsToast(apiError)) {
        const reference = apiError.correlationId
          ? ` ${t('error.reference', { id: apiError.correlationId })}`
          : '';
        toast.show(`${apiError.userMessage}${apiError.status >= 500 ? reference : ''}`, {
          tone: 'danger',
        });
      }
      return throwError((): ApiError => apiError);
    }),
  );
};

function needsToast(error: ApiError): boolean {
  return error.status === 0 || error.status === 403 || error.status === 429 || error.status >= 500;
}
